import { Link, useNavigate } from 'react-router-dom'

interface SearchResult {
  product: {
    id: string
    name: string
    description: string
    extractType: string
    type: string
    sizes: Array<{ weight: string; price: number; inStock: boolean }>
    images: string[]
    inStock: boolean
    featured: boolean
    categories: string[]
    tags: string[]
    dateAdded: Date
    salesCount: number
    rating: number
    reviewCount: number
    potency?: string
    effects?: string[]
  }
  vendor: {
    id: string
    name: string
    verified: boolean
    subscriptionPlan?: string
    rating: number
  }
  score: number
  isFeatured: boolean
  isPremiumVendor: boolean
}

interface SearchResultsGridProps {
  results: SearchResult[]
  loading?: boolean
}

export default function SearchResultsGrid({ results, loading = false }: SearchResultsGridProps) {
  const navigate = useNavigate()

  const handleSuggestionClick = (searchTerm: string) => {
    navigate(`/search?q=${encodeURIComponent(searchTerm)}`)
  }
  if (loading) {
    return (
      <div className="search-results-loading">
        <div className="loading-grid">
          {Array.from({ length: 6 }).map((_, i) => (
            <div key={i} className="loading-card">
              <div className="loading-shimmer"></div>
            </div>
          ))}
        </div>
      </div>
    )
  }

  if (results.length === 0) {
    return (
      <div className="no-results">
        <div className="no-results-content">
          <h3>No products found</h3>
          <p>Try adjusting your search terms or filters to find what you're looking for.</p>
          <div className="search-suggestions">
            <h4>Popular searches:</h4>
            <div className="suggestion-tags">
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Thai 7OH')}>Thai 7OH</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('MIT Salt')}>MIT Salt</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Euphoric effects')}>Euphoric effects</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Red Maeng Da')}>Red Maeng Da</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Energizing')}>Energizing</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('White Thai')}>White Thai</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Sedating')}>Sedating</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Green Bali')}>Green Bali</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('7OH Tablets')}>7OH Tablets</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Pain Relief')}>Pain Relief</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Kratom Capsules')}>Kratom Capsules</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Red Bali')}>Red Bali</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Focus')}>Focus</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Relaxing')}>Relaxing</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('MIT Extract')}>MIT Extract</span>
              <span className="suggestion-tag" onClick={() => handleSuggestionClick('Mood Boost')}>Mood Boost</span>
            </div>
          </div>
        </div>
      </div>
    )
  }

  const featuredResults = results.filter(r => r.isFeatured)
  const regularResults = results.filter(r => !r.isFeatured)

  return (
    <div className="search-results-grid">
      
      {/* Featured Products Section */}
      {featuredResults.length > 0 && (
        <div className="featured-section">
          <div className="section-header">
            <h3>Featured Products</h3>
            <span className="section-badge">Promoted</span>
          </div>
          <div className="products-grid featured-grid">
            {featuredResults.map((result, index) => (
              <ProductCard key={`featured-${result.product.id}`} result={result} featured={true} />
            ))}
          </div>
        </div>
      )}

      {/* Regular Results Section */}
      {regularResults.length > 0 && (
        <div className="regular-section">
          {featuredResults.length > 0 && (
            <div className="section-header">
              <h3>All Results</h3>
            </div>
          )}
          <div className="products-grid">
            {regularResults.map((result, index) => (
              <ProductCard key={`regular-${result.product.id}`} result={result} featured={false} />
            ))}
          </div>
        </div>
      )}

      <style>{`
        .search-results-grid {
          max-width: 1200px;
          margin: 0 auto;
        }

        .search-results-loading {
          max-width: 1200px;
          margin: 0 auto;
        }

        .loading-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
        }

        .loading-card {
          height: 400px;
          background: #f0f0f0;
          border-radius: 8px;
          overflow: hidden;
        }

        .loading-shimmer {
          width: 100%;
          height: 100%;
          background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
          background-size: 200% 100%;
          animation: shimmer 2s infinite;
        }

        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }

        .no-results {
          max-width: 600px;
          margin: 60px auto;
          text-align: center;
          padding: 40px;
          background: #f9f9f9;
          border-radius: 12px;
          border: 2px dashed #ddd;
        }

        .no-results-content h3 {
          color: #a0aec0;
          margin-bottom: 15px;
        }

        .no-results-content p {
          color: #a0aec0;
          margin-bottom: 30px;
        }

        .search-suggestions h4 {
          color: #e2e8f0;
          margin-bottom: 15px;
          font-size: 16px;
        }

        .suggestion-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 8px;
          justify-content: center;
        }

        .suggestion-tag {
          background: #e8f5e8;
          color: #1a202c;
          padding: 6px 12px;
          border-radius: 16px;
          font-size: 12px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .suggestion-tag:hover {
          background: #2c5530;
          color: white;
        }

        .featured-section {
          margin-bottom: 40px;
        }

        .section-header {
          display: flex;
          align-items: center;
          gap: 15px;
          margin-bottom: 20px;
          padding-bottom: 10px;
          border-bottom: 2px solid #e0e0e0;
        }

        .section-header h3 {
          color: #e2e8f0;
          margin: 0;
          font-size: 1.4em;
        }

        .section-badge {
          background: linear-gradient(135deg, #ffa726, #ff9800);
          color: white;
          padding: 4px 12px;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 600;
        }

        .products-grid {
          display: grid;
          grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
          gap: 20px;
        }

        .featured-grid {
          grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
        }

        @media (max-width: 768px) {
          .products-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
            gap: 15px;
          }

          .featured-grid {
            grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
          }

          .section-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 10px;
          }
        }
      `}</style>
    </div>
  )
}

function ProductCard({ result, featured }: { result: SearchResult; featured: boolean }) {
  const { product, vendor, isPremiumVendor } = result
  const minPrice = Math.min(...product.sizes.map(s => s.price))
  
  return (
    <div className={`product-card ${featured ? 'featured' : ''} ${isPremiumVendor ? 'premium-vendor' : ''}`}>
      <Link to={`/vendor/${vendor.id}/product/${product.id}`} className="product-link">
        
        {/* Product Image */}
        <div className="product-image-container">
          {product.images[0] ? (
            <img 
              src={product.images[0]} 
              alt={product.name}
              className="product-image"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling.style.display = 'flex';
              }}
            />
          ) : null}
          <div className="image-placeholder" style={{display: product.images[0] ? 'none' : 'flex'}}>
            <div className="placeholder-content">
              <span className="placeholder-icon">📦</span>
              <span className="placeholder-text">{product.extractType}</span>
            </div>
          </div>
          
          {/* Overlay Badges */}
          <div className="product-badges">
            {featured && (
              <span className="badge featured-badge">Featured</span>
            )}
            {isPremiumVendor && (
              <span className="badge premium-badge">Premium Vendor</span>
            )}
            {!product.inStock && (
              <span className="badge out-of-stock-badge">Out of Stock</span>
            )}
          </div>
          
          {/* Effects Icons */}
          {product.effects && product.effects.length > 0 && (
            <div className="effects-overlay">
              {product.effects.slice(0, 2).map(effect => (
                <span key={effect} className="effect-icon" title={effect}>
                  {getEffectIcon(effect)}
                </span>
              ))}
              {product.effects.length > 2 && (
                <span className="more-effects">+{product.effects.length - 2}</span>
              )}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="product-info">
          
          {/* Vendor Info */}
          <div className="vendor-info">
            <span className="vendor-name">{vendor.name}</span>
            {vendor.verified && <span className="verified-icon">✓</span>}
            <span className="vendor-rating">
              ★ {vendor.rating}
            </span>
          </div>

          {/* Product Name & Type */}
          <h4 className="product-name">{product.name}</h4>
          <div className="product-meta">
            <span className="extract-type">{product.extractType}</span>
            <span className="product-format">{product.type}</span>
          </div>

          {/* Potency */}
          {product.potency && (
            <div className="potency-info">
              <strong>Potency:</strong> {product.potency}
            </div>
          )}

          {/* Effects */}
          {product.effects && product.effects.length > 0 && (
            <div className="effects-list">
              <strong>Effects:</strong>
              <div className="effects-tags">
                {product.effects.map(effect => (
                  <span key={effect} className="effect-tag">{effect}</span>
                ))}
              </div>
            </div>
          )}

          {/* Rating & Reviews */}
          <div className="product-rating">
            {product.reviewCount > 0 ? (
              <>
                <span className="rating-stars">{'★'.repeat(Math.floor(product.rating))}</span>
                <span className="rating-score">{product.rating}</span>
                <span className="review-count">({product.reviewCount} reviews)</span>
              </>
            ) : (
              <span className="no-reviews">No reviews yet</span>
            )}
          </div>

          {/* Pricing */}
          <div className="pricing-info">
            <span className="price-from">From</span>
            <span className="price">${minPrice}</span>
            {product.sizes.length > 1 && (
              <span className="size-count">• {product.sizes.length} sizes</span>
            )}
          </div>

          {/* Tags */}
          <div className="product-tags">
            {product.tags.slice(0, 3).map(tag => (
              <span key={tag} className="product-tag">{tag}</span>
            ))}
            {product.tags.length > 3 && (
              <span className="more-tags">+{product.tags.length - 3}</span>
            )}
          </div>

          {/* Sales Info */}
          {product.salesCount > 0 && (
            <div className="sales-info">
              {product.salesCount} sold
            </div>
          )}
        </div>
      </Link>

      <style>{`
        .product-card {
          background: #2d3748;
          border-radius: 12px;
          box-shadow: 0 2px 8px rgba(0,0,0,0.1);
          overflow: hidden;
          transition: all 0.3s ease;
          border: 2px solid transparent;
        }

        .product-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 25px rgba(0,0,0,0.15);
        }

        .product-card.featured {
          border-color: #ffa726;
          box-shadow: 0 4px 15px rgba(255, 167, 38, 0.3);
        }

        .product-card.premium-vendor {
          border-color: #2c5530;
        }

        .product-card.featured.premium-vendor {
          border-image: linear-gradient(45deg, #ffa726, #2c5530) 1;
        }

        .product-link {
          display: block;
          text-decoration: none;
          color: inherit;
        }

        .product-image-container {
          position: relative;
          height: 200px;
          overflow: hidden;
        }

        .product-image {
          width: 100%;
          height: 100%;
          object-fit: cover;
          transition: transform 0.3s ease;
        }

        .image-placeholder {
          width: 100%;
          height: 100%;
          background: linear-gradient(135deg, #2d3748, #4a5568);
          display: flex;
          align-items: center;
          justify-content: center;
          color: #e2e8f0;
          transition: transform 0.3s ease;
        }

        .placeholder-content {
          text-align: center;
          padding: 20px;
        }

        .placeholder-icon {
          font-size: 3em;
          display: block;
          margin-bottom: 10px;
          opacity: 0.7;
        }

        .placeholder-text {
          font-size: 14px;
          font-weight: 500;
          opacity: 0.8;
        }

        .product-card:hover .product-image,
        .product-card:hover .image-placeholder {
          transform: scale(1.05);
        }

        .product-badges {
          position: absolute;
          top: 10px;
          left: 10px;
          display: flex;
          flex-direction: column;
          gap: 5px;
        }

        .badge {
          padding: 4px 8px;
          border-radius: 12px;
          font-size: 10px;
          font-weight: 600;
          text-transform: uppercase;
        }

        .featured-badge {
          background: linear-gradient(135deg, #ffa726, #ff9800);
          color: white;
        }

        .premium-badge {
          background: linear-gradient(135deg, #2c5530, #4caf50);
          color: white;
        }

        .out-of-stock-badge {
          background: rgba(244, 67, 54, 0.9);
          color: white;
        }

        .effects-overlay {
          position: absolute;
          top: 10px;
          right: 10px;
          display: flex;
          gap: 5px;
          align-items: center;
        }

        .effect-icon {
          background: rgba(255,255,255,0.9);
          border-radius: 50%;
          width: 24px;
          height: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 12px;
        }

        .more-effects {
          background: rgba(44, 85, 48, 0.9);
          color: white;
          border-radius: 10px;
          padding: 2px 6px;
          font-size: 10px;
          font-weight: 600;
        }

        .product-info {
          padding: 16px;
        }

        .vendor-info {
          display: flex;
          align-items: center;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 12px;
        }

        .vendor-name {
          color: #a0aec0;
          font-weight: 500;
        }

        .verified-icon {
          color: #4caf50;
          font-weight: bold;
        }

        .vendor-rating {
          color: #ffa726;
          font-weight: 500;
        }

        .product-name {
          color: #e2e8f0;
          margin: 0 0 8px 0;
          font-size: 16px;
          font-weight: 600;
          line-height: 1.3;
          text-align: center;
        }

        .product-meta {
          display: flex;
          gap: 8px;
          margin-bottom: 8px;
          font-size: 12px;
          justify-content: center;
        }

        .extract-type {
          background: #e8f5e8;
          color: #1a202c;
          padding: 2px 6px;
          border-radius: 8px;
          font-weight: 500;
        }

        .product-format {
          background: #f0f0f0;
          color: #a0aec0;
          padding: 2px 6px;
          border-radius: 8px;
          text-transform: capitalize;
        }

        .potency-info {
          font-size: 12px;
          color: #e2e8f0;
          margin-bottom: 8px;
        }

        .effects-list {
          margin-bottom: 10px;
          font-size: 12px;
        }

        .effects-list strong {
          color: #e2e8f0;
        }

        .effects-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-top: 4px;
        }

        .effect-tag {
          background: #2d37483e0;
          color: #f57c00;
          padding: 2px 6px;
          border-radius: 6px;
          font-size: 10px;
          font-weight: 500;
        }

        .product-rating {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 12px;
          font-size: 12px;
        }

        .rating-stars {
          color: #ffa726;
          font-size: 14px;
        }

        .rating-score {
          font-weight: 600;
          color: #e2e8f0;
        }

        .review-count {
          color: #a0aec0;
        }

        .no-reviews {
          color: #a0aec0;
          font-style: italic;
        }

        .pricing-info {
          display: flex;
          align-items: center;
          gap: 6px;
          margin-bottom: 12px;
          font-size: 14px;
        }

        .price-from {
          color: #a0aec0;
          font-size: 12px;
        }

        .price {
          font-size: 18px;
          font-weight: bold;
          color: #00d166;
        }

        .size-count {
          color: #a0aec0;
          font-size: 12px;
        }

        .product-tags {
          display: flex;
          flex-wrap: wrap;
          gap: 4px;
          margin-bottom: 8px;
        }

        .product-tag {
          background: #f5f5f5;
          color: #a0aec0;
          padding: 2px 6px;
          border-radius: 6px;
          font-size: 10px;
        }

        .more-tags {
          background: #e0e0e0;
          color: #a0aec0;
          padding: 2px 6px;
          border-radius: 6px;
          font-size: 10px;
        }

        .sales-info {
          font-size: 11px;
          color: #a0aec0;
          text-align: right;
        }

        @media (max-width: 768px) {
          .product-image-container {
            height: 160px;
          }
          
          .product-info {
            padding: 12px;
          }
          
          .product-name {
            font-size: 14px;
          }
          
          .pricing-info .price {
            font-size: 16px;
          }
        }
      `}</style>
    </div>
  )
}

function getEffectIcon(effect: string): string {
  switch (effect.toLowerCase()) {
    case 'sedating': return '😴'
    case 'energetic': return '⚡'
    case 'euphoric': return '😊'
    case 'pain relieving': return '🩹'
    default: return '✨'
  }
}